import React, { useEffect, useState } from "react";
import {
  Text,
  StyleSheet,
  View,
  TextInput,
  TouchableOpacity,
  Platform,
} from "react-native";
import { PieChart } from "react-native-svg-charts";

import moment from "moment";

import { HomeCardGroups, isWeb } from "../../../../helpers/utils.js";

import cardStyles from "./cardStyles";
import MoodsList from "../../common/mood/MoodsList.js";
import ManaButton from "../../common/ManaButton.js";
import Collapsible from "../../common/Collapsible/Collapsible.js";
import AppTheme from "../../AppTheme.js";
import ManaHourMinutes from "../../common/ManaHourMinutes";

export default function ReflectionPieChart({
  data,
  showIntervalText = true,
  moodText = "How do you feel?",
  saveCardData = () => {},
  onChartPress = () => {},
}) {
  if (!data) return null;

  const [note, setNote] = useState(data.gem_text);
  const [mood, setMood] = useState(data.gem_mood);

  useEffect(() => {
    setNote(data.gem_text || "");
    setMood(data.gem_mood || "");
  }, [data.gem_text, data.gem_mood]);

  const { from: start_day, to: end_day } = data || {};
  const from = moment(start_day).format("MMM D");
  const to = moment(end_day).format("MMM D");
  const intervalText = from === to ? from : from + " - " + to;

  const groups = HomeCardGroups;
  const pieData = Object.keys(groups).map((key, index) => ({
    name: key,
    key: `pie-${index}`,
    value: data[`${key}_total`] || 0,
    progress: (data[`${key}_pct`] || 0).toFixed(0),
    icon: groups[key].icon,
    color: groups[key].color,
    svg: {
      fill: groups[key].color,
    },
  }));

  return (
    <View>
      {/* Header */}
      {showIntervalText ? (
        <View style={[cardStyles.header, { paddingHorizontal: 0 }]}>
          <View style={[cardStyles.headerInfo, { marginLeft: 0 }]}>
            <Text style={[cardStyles.title]}>The Big Picture</Text>
            <Text style={cardStyles.date}>{intervalText}</Text>
          </View>
        </View>
      ) : null}

      <View style={styles.content}>
        {/* Data */}
        <View style={{ flex: 1 }}>
          {pieData.map((g, index) => (
            <View key={index} style={[styles.dataRow]}>
              <View style={[styles.colorTile, { backgroundColor: g.color }]} />
              <g.icon width={16} height={16} color={AppTheme.almostBlack} />
              <Text style={styles.name}>{g.name}</Text>
              <Text style={styles.progress}>
                {(g.progress > 0 ? "+" : "") + g.progress}%
              </Text>
              <View style={{ minWidth: 40 }}>
                <ManaHourMinutes style={styles.hours} duration={g.value} />
              </View>
            </View>
          ))}
        </View>

        {/* Chart */}
        <TouchableOpacity onPress={onChartPress}>
          <PieChart
            style={styles.chart}
            data={pieData}
            innerRadius={"60%"}
            outerRadius={"100%"}
            sort={(a, b) => (b.key < a.key ? 1 : -1)}
          />
        </TouchableOpacity>
      </View>

      {/* Journal */}
      <View style={cardStyles.separator} />

      <View>
        <MoodsList
          selectedMood={mood}
          onSelect={(m) => setMood(m)}
          showTitle
          title={moodText}
        />

        <Collapsible isCollapsed={!mood}>
          <View style={cardStyles.messageContainer}>
            <TextInput
              name={"reflectionNote"}
              style={cardStyles.input}
              autoFocus={false}
              multiline={true}
              numberOfLines={2}
              value={note}
              onChangeText={(text) => setNote(text)}
              placeholderTextColor="#9C9EB9"
              placeholder="Enter a quick thought on how this week makes you feel..."
            />
          </View>

          <View style={[cardStyles.buttons, { paddingBottom: 16 }]}>
            <ManaButton
              onPress={() => saveCardData(mood, note)}
              style={cardStyles.buttonSmall}
              styleText={cardStyles.buttonSmallText}
              text="Journal"
            />
          </View>
        </Collapsible>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  content: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  chart: {
    width: 120,
    height: 120,
  },
  dataRow: {
    display: "flex",
    alignItems: "center",
    flexDirection: "row",
    height: 30,
    paddingRight: 8,
    marginRight: isWeb ? 13 : 5,
  },
  colorTile: {
    height: 24,
    width: 6,
    marginRight: 2,
  },
  name: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-SemiBold",
    }),
    fontWeight: "600",
    fontSize: 14,
    lineHeight: 17,
    letterSpacing: 0.175,
    color: AppTheme.almostBlack,
    textTransform: "capitalize",
    flex: 1,
    marginLeft: 6,
  },
  progress: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-SemiBold",
    }),
    fontWeight: "600",
    fontSize: 12,
    lineHeight: 15,
    letterSpacing: 0.175,
    color: AppTheme.almostBlack,
    marginRight: 6,
    borderRightColor: AppTheme.borderColor,
    borderRightWidth: 1,
    maxWidth: 60,
    textAlign: "right",
    paddingRight: 3,
  },
});
